import React, {useEffect, useState, useRef, RefObject} from 'react';
import cx from 'classnames';

import Autocomplete from './LEGACY_Autocomplete';
import Icon from '../icon/Icon';
import Link from '../link/Link';
import {getMessage} from '../message/Message';
import {breakpoints, keyCodes, searchSettings, sizes, svgs} from '../../constants';
import {AutocompleteProps, MaxCounts} from './interfaces';

// @ts-ignore
import styles from './LEGACY_SearchBar.pcss';
import {Breakpoint} from '../../constants/Breakpoint';

export interface SearchBarProps {
    breakpoint?: Breakpoint;
    inResultsPage?: boolean;
    lastSearch?: string;
    messages: any;
    search: Function;
    searchResults: any[];
    setSearchText: Function;
    text: string;
}

interface SubmitOptions {
    inputRef: RefObject<HTMLInputElement>;
    query: string;
    search: Function;
    setSearchText: Function;
    setTriggerAutocompleteFocus: Function;
    setUseLastSearch: Function;
}

interface ComputeCustomWidthOptions {
    canSubmit: boolean;
    placeholderWidthRef: RefObject<HTMLDivElement>;
    useCustomWidth: boolean;
}

interface CustomWidths {
    customWidth?: number;
    customAutocompleteWidth?: number;
}

interface NavLink {
    path: string;
    text: string;
}

// Additional width to account for icon offsets.  (Needed for broad click area padding.)
const AUTOCOMPLETE_WIDTH_RATIO = 1.39;

// See SearchBar.pcss
const SEARCH_FIELD_PADDING = 6;

const largeOrGreater = (breakpoint: Breakpoint): boolean =>
    [breakpoints.name.LARGE, breakpoints.name.XLARGE, breakpoints.name.MAX].includes(breakpoint);

const maxCountsByScreenSize = (breakpoint: Breakpoint, numResults: number): MaxCounts => {
    return largeOrGreater(breakpoint)
        ? {
              maxResults: searchSettings.MAX_SUGGESTIONS,
              maxIndex: searchSettings.MAX_SUGGESTIONS // Allow additional focus index for "See More"
          }
        : {
              maxResults: numResults,
              maxIndex: numResults - 1
          };
};

const shouldUseCustomWidth = (
    breakpoint: Breakpoint,
    placeholderWidthRef: RefObject<HTMLDivElement>
): boolean => !!(placeholderWidthRef && placeholderWidthRef.current && largeOrGreater(breakpoint));

const computeCustomWidth = ({
    canSubmit,
    placeholderWidthRef,
    useCustomWidth
}: ComputeCustomWidthOptions): CustomWidths => {
    if (!useCustomWidth) return {};

    const {width} =
        placeholderWidthRef && placeholderWidthRef.current
            ? placeholderWidthRef.current.getBoundingClientRect()
            : {width: 0};

    if (!canSubmit) {
        return {
            customWidth: width + SEARCH_FIELD_PADDING,
            customAutocompleteWidth: undefined
        };
    }

    return {
        customWidth: width + SEARCH_FIELD_PADDING,
        customAutocompleteWidth: width * AUTOCOMPLETE_WIDTH_RATIO
    };
};

const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setSearchText: Function,
    setUseLastSearch: Function
): void => {
    const target: HTMLInputElement = e && e.currentTarget;
    if (target) {
        setSearchText(target.value);
        setUseLastSearch(false);
    }
};

const submitSearch = (submitOptions: SubmitOptions): void => {
    const {search, inputRef, query} = submitOptions;
    search(query);
    if (!inputRef || !inputRef.current) return;
    inputRef.current.blur();
};

const clearSearch = (
    inputRef: RefObject<HTMLInputElement>,
    setSearchText: Function,
    setUseLastSearch: Function
): void => {
    setSearchText('');
    setUseLastSearch(false);
    if (!inputRef || !inputRef.current) return;
    inputRef.current.focus();
};

const submitFromInput = (
    canSubmit: boolean,
    e: React.KeyboardEvent<HTMLInputElement>,
    submitOptions: SubmitOptions
): void => {
    if (!canSubmit) return;

    const {inputRef, setSearchText, setTriggerAutocompleteFocus, setUseLastSearch} = submitOptions;

    if (e.keyCode === keyCodes.ESCAPE) {
        clearSearch(inputRef, setSearchText, setUseLastSearch);
        return;
    }
    if (e.keyCode === keyCodes.DOWN) {
        e.preventDefault();
        setTriggerAutocompleteFocus(true);
        return;
    }
    if (e.keyCode !== keyCodes.RETURN) return;
    e.preventDefault();
    submitSearch(submitOptions);
};

const submitFromButton = (canSubmit: boolean, submitOptions: SubmitOptions): void => {
    if (!canSubmit) return;
    submitSearch(submitOptions);
};

const SearchNav = (props: {messages: any; inResultsPage?: boolean; navLinks: NavLink[]}) => {
    const {messages, inResultsPage, navLinks} = props;

    const storeLabelText = getMessage(messages, 'diesel.common.search.storeLabel');

    if (!inResultsPage) {
        return <div className={styles.title}>{storeLabelText}</div>;
    }

    return (
        <div className={styles.searchNav}>
            {navLinks.map(navLink => (
                <Link
                    to={navLink.path}
                    className={cx(styles.link, styles.back)}
                    aria-label={navLink.text}
                    key={navLink.text}>
                    <span className={styles.arrow}>
                        <Icon
                            icon={svgs.CHEVRON}
                            size={sizes.CUSTOM}
                            className={styles.arrowIcon}
                        />
                    </span>
                    {navLink.text}
                </Link>
            ))}
        </div>
    );
};

export const SearchBar = (props: SearchBarProps) => {
    const {
        breakpoint = Breakpoint.LARGE,
        inResultsPage,
        lastSearch,
        messages,
        search,
        searchResults,
        setSearchText,
        text
    } = props;
    const [useLastSearch, setUseLastSearch] = useState(true);
    const [triggerAutocompleteFocus, setTriggerAutocompleteFocus] = useState(false);
    const inputRef: RefObject<HTMLInputElement> = useRef<HTMLInputElement>(null);
    const searchBarRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
    const placeholderWidthRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!props.inResultsPage) {
            props.setSearchText('');
        }
    }, []);

    const inputValue: string = useLastSearch && lastSearch ? lastSearch : text;
    const canSubmit = !!(inputValue && inputValue.length > 0);
    const showAutocomplete: boolean = canSubmit && !useLastSearch;
    const numResults: number = (searchResults && searchResults.length) || 0;
    const maxCounts: MaxCounts = maxCountsByScreenSize(breakpoint, numResults);

    const useCustomWidth: boolean = shouldUseCustomWidth(breakpoint, placeholderWidthRef);
    const {customWidth, customAutocompleteWidth}: CustomWidths = computeCustomWidth({
        canSubmit,
        placeholderWidthRef,
        useCustomWidth
    });

    const searchWrapperClasses: string = cx(styles.searchBarWrapper, {
        [styles.showClose]: canSubmit
    });

    const searchBarClasses: string = cx(styles.searchBar, styles.isOpen);

    const submitOptions: SubmitOptions = {
        inputRef,
        query: text,
        search,
        setSearchText,
        setTriggerAutocompleteFocus,
        setUseLastSearch
    };

    const autocompleteProps: AutocompleteProps & {customWidth?: number} = {
        clearSearchBar: () => clearSearch(inputRef, setSearchText, setUseLastSearch),
        customWidth: customAutocompleteWidth,
        maxCounts,
        maxSuggestions: searchSettings.MAX_SUGGESTIONS,
        messages,
        searchResults,
        text: inputValue,
        triggerFocus: triggerAutocompleteFocus
    };

    const placeholderText: string = getMessage(messages, 'diesel.common.search.placeholder');
    const ariaLabelText: string = getMessage(messages, 'diesel.common.search.ariaLabel');
    const ariaClearLabelText: string = getMessage(messages, 'diesel.common.search.ariaClearLabel');

    const navLinks = [
        {
            path: '/store',
            text: getMessage(messages, 'diesel.main.navigation.back_to_store')
        }
    ];

    return (
        <div className={styles.searchContainer}>
            <div className={searchWrapperClasses}>
                <SearchNav inResultsPage={inResultsPage} messages={messages} navLinks={navLinks} />
                <div className={searchBarClasses} ref={searchBarRef}>
                    <div className={styles.searchForm}>
                        <button
                            className={styles.searchOpenButton}
                            aria-label={ariaLabelText}
                            tabIndex={-1}
                            onClick={() => submitFromButton(canSubmit, submitOptions)}>
                            <Icon
                                icon={svgs.SEARCH}
                                className={styles.searchIcon}
                                size={sizes.CUSTOM}
                            />
                        </button>
                        <input
                            ref={inputRef}
                            placeholder={placeholderText}
                            aria-label={ariaLabelText}
                            autoComplete="off"
                            maxLength={searchSettings.MAX_CHAR_COUNT}
                            className={styles.searchField}
                            id="searchInput"
                            style={{width: customWidth}}
                            value={inputValue}
                            onChange={e => handleChange(e, setSearchText, setUseLastSearch)}
                            onKeyDown={e => submitFromInput(canSubmit, e, submitOptions)}
                            onBlur={() => setTriggerAutocompleteFocus(false)}
                        />
                        <div
                            className={styles.placeholderWidth}
                            ref={placeholderWidthRef}
                            tabIndex={-1}>
                            {placeholderText}
                        </div>
                        <button
                            className={styles.searchCloseButton}
                            aria-label={ariaClearLabelText}
                            onClick={() => clearSearch(inputRef, setSearchText, setUseLastSearch)}>
                            <Icon
                                icon={svgs.CLOSE}
                                className={styles.searchIcon}
                                size={sizes.CUSTOM}
                            />
                        </button>
                    </div>
                </div>
            </div>
            {showAutocomplete && <Autocomplete {...autocompleteProps} ref={inputRef} />}
        </div>
    );
};

export default SearchBar;
